import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
    Dns as DnsIcon,
    Refresh as RefreshIcon,
    AccessTime as TimeIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    InfoOutlined as InfoIcon,
    CloudQueue as CloudIcon,
    Storage as StorageIcon,
    Analytics as AnalyticsIcon,
    Email as EmailIcon,
    Sms as SmsIcon
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import configData from '../../configure';
import './ServicesStatus.css';

const ServicesStatus = () => {
    // Set Page Title for SEO
    useEffect(() => {
        document.title = "System Services Status | eGurukul";
    }, []);

    const [services, setServices] = useState({
        identity: {
            name: "Campus Identity Service",
            key: "identity",
            endpoint: configData.IDENTITY_HEALTH || "https://org1.egurukulapps.com/campusidentity/api/health",
            description: "Handles user profiles, permissions, roles, authentication sessions, and Firebase token sync.",
            status: "Not Checked",
            latency: null,
            history: Array.from({ length: 30 }, (_, i) => ({ day: i + 1, status: "Operational" })),
            latencyHistory: [45, 52, 60, 48, 55, 47, 53, 50, 49, 58]
        },
        resources: {
            name: "Campus Resources Service",
            key: "resources",
            endpoint: configData.RESOURCES_HEALTH || "https://org1.egurukulapps.com/campusresources/api/health",
            description: "Manages class schedules, courses, enrollment records, event timelines, and student performance metrics.",
            status: "Not Checked",
            latency: null,
            history: Array.from({ length: 30 }, (_, i) => ({ day: i + 1, status: "Operational" })),
            latencyHistory: [88, 92, 105, 96, 110, 89, 95, 99, 91, 102]
        },
        infrastructure: {
            name: "Campus Infrastructure Service",
            key: "infrastructure",
            endpoint: configData.INFRASTRUCTURE_HEALTH || "https://org1.egurukulapps.com/campusinfrastructure/api/health",
            description: "Controls notification broadcasts (Email/SMS), message inbox threads, fee planning, and payment gateways.",
            status: "Not Checked",
            latency: null,
            history: Array.from({ length: 30 }, (_, i) => ({ day: i + 1, status: "Operational" })),
            latencyHistory: [64, 70, 82, 75, 79, 68, 72, 74, 69, 77]
        }
    });

    const [isRefreshing, setIsRefreshing] = useState(false);
    const [overallStatus, setOverallStatus] = useState("Not Checked");
    const [lastChecked, setLastChecked] = useState("Not checked yet");

    const checkService = async (serviceKey, endpoint) => {
        const startTime = Date.now();
        try {
            // Use axios with a reasonable timeout of 4 seconds
            const response = await axios.get(endpoint, { timeout: 4000 });
            const endTime = Date.now();
            const latency = endTime - startTime;

            if (response.status >= 200 && response.status < 300) {
                return { status: "Operational", latency };
            }
            return { status: "Degraded", latency, error: `HTTP ${response.status}` };
        } catch (error) {
            const endTime = Date.now();
            console.error(`Health check failed for ${serviceKey}:`, error.message);
            return { 
                status: "Down", 
                latency: Math.max(10, endTime - startTime), 
                error: error.message || "Network Error" 
            };
        }
    };

    const runHealthChecks = useCallback(async () => {
        setIsRefreshing(true);
        const updatedServices = { ...services };

        const identityResult = await checkService("identity", updatedServices.identity.endpoint);
        const resourcesResult = await checkService("resources", updatedServices.resources.endpoint);
        const infrastructureResult = await checkService("infrastructure", updatedServices.infrastructure.endpoint);

        // Update Identity
        updatedServices.identity = {
            ...updatedServices.identity,
            status: identityResult.status,
            latency: identityResult.latency,
            latencyHistory: [...updatedServices.identity.latencyHistory.slice(1), identityResult.latency]
        };

        // Update Resources
        updatedServices.resources = {
            ...updatedServices.resources,
            status: resourcesResult.status,
            latency: resourcesResult.latency,
            latencyHistory: [...updatedServices.resources.latencyHistory.slice(1), resourcesResult.latency]
        };

        // Update Infrastructure
        updatedServices.infrastructure = {
            ...updatedServices.infrastructure,
            status: infrastructureResult.status,
            latency: infrastructureResult.latency,
            latencyHistory: [...updatedServices.infrastructure.latencyHistory.slice(1), infrastructureResult.latency]
        };

        // Calculate overall status
        const statuses = [identityResult.status, resourcesResult.status, infrastructureResult.status];
        if (statuses.every(s => s === "Operational")) {
            setOverallStatus("All Systems Operational");
        } else if (statuses.some(s => s === "Down")) {
            setOverallStatus("Partial Service Disruption");
        } else {
            setOverallStatus("Degraded System Performance");
        }

        setServices(updatedServices);
        setLastChecked(new Date().toLocaleTimeString());
        setIsRefreshing(false);
    }, [services]);

    useEffect(() => {
        // Page title set on mount; APIs only fetch when clicking 'Test Now'
    }, []);

    const getStatusIcon = (status) => {
        switch (status) {
            case "Operational":
                return <CheckCircleIcon className="status-icon text-success" />;
            case "Degraded":
                return <WarningIcon className="status-icon text-warning" />;
            case "Down":
                return <ErrorIcon className="status-icon text-danger" />;
            case "Not Checked":
                return <InfoIcon className="status-icon text-muted" style={{ color: '#94a3b8' }} />;
            default:
                return <CircularProgress size={18} className="status-icon-spinner" />;
        }
    };

    // Helper to generate coordinates for SVG chart lines
    const getSvgPoints = (history, width, height) => {
        if (!history || history.length === 0) return "";
        const maxVal = Math.max(...history, 120); // Scale relative to max latency or 120ms
        const stepX = width / (history.length - 1);
        
        return history.map((val, idx) => {
            const x = idx * stepX;
            // Invert Y axis for SVGs: higher latency = lower visual position
            const y = height - (val / maxVal) * (height - 10) - 5;
            return `${x},${y}`;
        }).join(" ");
    };

    return (
        <div className="status-page-container" id="system-status-page">
            {/* Header Title Card */}
            <div className="status-header-card">
                <div className="header-icon-wrap">
                    <DnsIcon className="dns-header-icon" />
                </div>
                <div className="header-text-wrap">
                    <h1 id="status-title-heading">Service Registry & Health Metrics</h1>
                    <p>Live health logs, routing endpoints, and connection latencies across eGurukul backend clusters.</p>
                </div>
                <button 
                    className={`refresh-status-btn ${isRefreshing ? 'refresh-loading' : ''}`}
                    onClick={runHealthChecks}
                    disabled={isRefreshing}
                    id="refresh-health-btn"
                >
                    {isRefreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                    <span>{isRefreshing ? 'Testing...' : 'Test Now'}</span>
                </button>
            </div>

            {/* Overall Status Banner */}
            <div className={`overall-status-banner ${overallStatus.toLowerCase().replace(/ /g, '-')}`} id="overall-status-banner">
                <div className="banner-pulse">
                    <div className="pulse-circle"></div>
                </div>
                <span className="banner-text">{overallStatus}</span>
                <span className="last-checked-badge">
                    <TimeIcon fontSize="small" /> Checked: {lastChecked}
                </span>
            </div>

            {/* Core Services Grid */}
            <div className="services-grid" id="core-services-grid">
                {Object.values(services).map(service => (
                    <div key={service.key} className={`service-card ${service.status.toLowerCase()}`} id={`card-${service.key}`}>
                        <div className="card-header">
                            <div>
                                <h3 className="service-title">{service.name}</h3>
                            </div>
                            <div className="status-indicator-wrap">
                                <span className={`status-text-badge ${service.status.toLowerCase()}`}>
                                    {service.status}
                                </span>
                                {getStatusIcon(service.status)}
                            </div>
                        </div>

                        <p className="service-desc">{service.description}</p>

                        {/* Latency Plot */}
                        <div className="latency-sparkline-wrap">
                            <div className="sparkline-header">
                                <span className="sparkline-title">Response Latency Trend</span>
                                <span className="sparkline-value">
                                    {service.latency !== null ? `${service.latency}ms` : '-- ms'}
                                </span>
                            </div>
                            <div className="sparkline-svg-container">
                                <svg viewBox="0 0 200 45" className="sparkline-svg">
                                    <defs>
                                        <linearGradient id={`grad-${service.key}`} x1="0%" y1="0%" x2="0%" y2="100%">
                                            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                                            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.0" />
                                        </linearGradient>
                                    </defs>
                                    {/* Fill Area */}
                                    <polygon 
                                        points={`0,45 ${getSvgPoints(service.latencyHistory, 200, 45)} 200,45`}
                                        fill={`url(#grad-${service.key})`}
                                    />
                                    {/* Line */}
                                    <polyline
                                        fill="none"
                                        stroke="#3b82f6"
                                        strokeWidth="2.5"
                                        points={getSvgPoints(service.latencyHistory, 200, 45)}
                                    />
                                </svg>
                            </div>
                        </div>

                        {/* Uptime bars grid (Simulated 90 days) */}
                        <div className="uptime-grid-wrap">
                            <div className="uptime-header">
                                <span>Uptime Timeline (Last 30 Checks)</span>
                                <span>100%</span>
                            </div>
                            <div className="uptime-bars">
                                {service.history.map(day => (
                                    <div 
                                        key={day.day} 
                                        className={`uptime-bar ${service.status === 'Down' && day.day === 30 ? 'down' : 'operational'}`} 
                                        title={`Check ${day.day}: ${service.status === 'Down' && day.day === 30 ? 'Down' : 'Operational'}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>



            {/* Troubleshooting Alert Info */}
            <div className="troubleshoot-info-box" id="troubleshoot-info-box">
                <InfoIcon className="info-icon" />
                <div>
                    <h4>Routing & Connectivity Note</h4>
                    <p>All client pings are executed directly from your browser client using secure CORS pathways. In case a microservice shows a "Down" state, verify that you have proper local VPN/internal routing permissions allowed, or contact the infrastructure support team.</p>
                </div>
            </div>
        </div>
    );
};

export default ServicesStatus;
